import { useMoneyFormatter } from "@/lib/money-format";
import { useEffect, useMemo, useState } from "react";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { ArrowRight, Loader2, Search, Truck } from "lucide-react";
import { toast } from "sonner";
import { RequireAuth } from "@/components/RequireAuth";
import { AppShell } from "@/components/layout/AppShell";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  useBalances,
  useCreateTransfer,
  useWarehouses,
} from "@/lib/api/inventory.queries";
import { useProducts } from "@/lib/api/products.queries";
import { useActiveCompany } from "@/lib/company-context";
import { fmtQty } from "@/components/inventory/format";

export const Route = createFileRoute(
  "/companies/$companyId/inventario/traslados/new",
)({
  component: NewTransferPage,
});

function NewTransferPage() {
  return (
    <RequireAuth>
      <AppShell>
        <NewTransferContent />
      </AppShell>
    </RequireAuth>
  );
}

function NewTransferContent() {
  const fmtCop2 = useMoneyFormatter();
  const fmtCop = useMoneyFormatter();
  const { companyId } = Route.useParams();
  const navigate = useNavigate();
  const { setActiveCompanyId } = useActiveCompany();
  useEffect(() => {
    if (companyId) setActiveCompanyId(companyId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const productsQuery = useProducts(companyId, { pageSize: 200 });
  const warehousesQuery = useWarehouses(companyId);
  const createTransfer = useCreateTransfer(companyId);

  const [productSearch, setProductSearch] = useState("");
  const [productId, setProductId] = useState("");
  const [fromWarehouseId, setFromWarehouseId] = useState("");
  const [toWarehouseId, setToWarehouseId] = useState("");
  const [qty, setQty] = useState<string>("");
  const [notes, setNotes] = useState("");

  const stockableProducts = useMemo(
    () =>
      (productsQuery.data?.products ?? []).filter(
        (p) => p.kind === "product" && p.status === "active",
      ),
    [productsQuery.data],
  );
  const filteredProducts = useMemo(() => {
    if (!productSearch.trim()) return stockableProducts.slice(0, 50);
    const needle = productSearch.trim().toLowerCase();
    return stockableProducts
      .filter(
        (p) =>
          p.name.toLowerCase().includes(needle) ||
          p.sku?.toLowerCase().includes(needle),
      )
      .slice(0, 50);
  }, [stockableProducts, productSearch]);

  const activeWarehouses = (warehousesQuery.data?.warehouses ?? []).filter(
    (w) => w.isActive,
  );
  const sourceBalanceQuery = useBalances(companyId, {
    productId: productId || undefined,
    warehouseId: fromWarehouseId || undefined,
    includeZero: true,
    pageSize: 5,
  });
  const sourceBalance = sourceBalanceQuery.data?.balances[0];

  function submit(e: React.FormEvent) {
    e.preventDefault();
    const qtyNum = Number(qty);
    if (!productId || !fromWarehouseId || !toWarehouseId || !qtyNum) {
      toast.error("Completa todos los campos.");
      return;
    }
    if (fromWarehouseId === toWarehouseId) {
      toast.error("El origen y el destino deben ser bodegas distintas.");
      return;
    }
    createTransfer.mutate(
      {
        productId,
        fromWarehouseId,
        toWarehouseId,
        qty: qtyNum,
        notes: notes || undefined,
      },
      {
        onSuccess: () => {
          toast.success("Traslado registrado");
          void navigate({
            to: "/companies/$companyId/inventario/movimientos",
            params: { companyId },
          });
        },
        onError: (err) =>
          toast.error("No pudimos registrar el traslado", {
            description: err.message,
          }),
      },
    );
  }

  return (
    <div className="mx-auto flex max-w-3xl flex-col gap-6">
      <header className="space-y-1.5">
        <Link
          to="/companies/$companyId/inventario"
          params={{ companyId }}
          className="inline-flex w-fit items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
        >
          ← Volver a Inventario
        </Link>
        <div className="flex items-center gap-2">
          <Truck className="h-5 w-5 text-primary" />
          <h1 className="text-2xl font-semibold tracking-tight">
            Traslado entre bodegas
          </h1>
        </div>
        <p className="text-sm text-muted-foreground">
          Genera un par de movimientos atómico (salida del origen + entrada al
          destino) al costo promedio actual del origen. Si el origen no tiene
          saldo suficiente, el traslado se rechaza.
        </p>
      </header>

      <form className="flex flex-col gap-4" onSubmit={submit}>
        <Card>
          <CardContent className="flex flex-col gap-4 py-4">
            <div>
              <Label>Producto</Label>
              <div className="relative mt-1">
                <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  className="pl-8"
                  placeholder="Buscar por nombre o SKU..."
                  value={productSearch}
                  onChange={(e) => setProductSearch(e.target.value)}
                />
              </div>
              <select
                className="mt-2 w-full rounded border bg-background px-2 py-2 text-sm"
                size={5}
                value={productId}
                onChange={(e) => setProductId(e.target.value)}
              >
                {filteredProducts.length === 0 && (
                  <option disabled value="">
                    Sin resultados
                  </option>
                )}
                {filteredProducts.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                    {p.sku ? ` · ${p.sku}` : ""}
                  </option>
                ))}
              </select>
            </div>

            <div className="grid items-end gap-3 sm:grid-cols-[1fr_auto_1fr]">
              <div>
                <Label>Origen</Label>
                <select
                  className="mt-1 w-full rounded border bg-background px-2 py-2 text-sm"
                  value={fromWarehouseId}
                  onChange={(e) => setFromWarehouseId(e.target.value)}
                >
                  <option value="">— Bodega origen —</option>
                  {activeWarehouses.map((w) => (
                    <option key={w.id} value={w.id}>
                      {w.code} — {w.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex items-center justify-center pb-1.5">
                <ArrowRight className="h-5 w-5 text-muted-foreground" />
              </div>
              <div>
                <Label>Destino</Label>
                <select
                  className="mt-1 w-full rounded border bg-background px-2 py-2 text-sm"
                  value={toWarehouseId}
                  onChange={(e) => setToWarehouseId(e.target.value)}
                >
                  <option value="">— Bodega destino —</option>
                  {activeWarehouses
                    .filter((w) => w.id !== fromWarehouseId)
                    .map((w) => (
                      <option key={w.id} value={w.id}>
                        {w.code} — {w.name}
                      </option>
                    ))}
                </select>
              </div>
            </div>

            {sourceBalance && (
              <div className="rounded-md border bg-muted/30 p-3 text-sm">
                <div className="mb-1 flex items-center justify-between">
                  <span className="text-muted-foreground">
                    Saldo actual en{" "}
                    <Badge variant="muted" className="font-mono text-[10px]">
                      {sourceBalance.warehouseCode}
                    </Badge>
                  </span>
                  <span className="font-semibold tabular-nums">
                    {fmtQty(sourceBalance.qty)} unidades
                  </span>
                </div>
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span>Costo promedio</span>
                  <span className="tabular-nums">
                    {fmtCop2(sourceBalance.avgUnitCost)} · valor{" "}
                    {fmtCop(sourceBalance.totalValue)}
                  </span>
                </div>
              </div>
            )}

            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <Label>Cantidad a trasladar</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.000001"
                  value={qty}
                  onChange={(e) => setQty(e.target.value)}
                  placeholder="5"
                />
                {sourceBalance && qty && Number(qty) > sourceBalance.qty && (
                  <p className="mt-1 text-xs text-destructive">
                    La cantidad excede el saldo disponible.
                  </p>
                )}
              </div>
              <div>
                <Label>Notas (opcional)</Label>
                <Input
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Reposición sucursal"
                  maxLength={1000}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-end gap-2">
          <Button asChild variant="ghost">
            <Link
              to="/companies/$companyId/inventario"
              params={{ companyId }}
            >
              Cancelar
            </Link>
          </Button>
          <Button type="submit" disabled={createTransfer.isPending}>
            {createTransfer.isPending && (
              <Loader2 className="h-4 w-4 animate-spin" />
            )}
            <Truck className="h-4 w-4" />
            Trasladar
          </Button>
        </div>
      </form>
    </div>
  );
}
